import type { CSSProperties } from "react"
import { cardFace, parseCardId } from "@/content/cards"
import type { CardId } from "@/types"
import styles from "@/styles/components/DeckCard.module.css"

/**
 * The deck-browser card face — a web recreation of the printed card art, per the
 * Figma handoff's `kc()` / `.kc`/`.kcb`.
 *
 * This is NOT `CardFace`. `CardFace` is the card the room picks with in a session
 * and is shared across the board, deep dive and report; this one is browser-only,
 * so the browser can be reskinned without touching the pick exercise. The two
 * differ on purpose: here the eyebrow is the CAPABILITY name (not the family name
 * + number), there is no number and no family name on the face, and the whole
 * thing is sized in container-query units so ONE element scales from a 54px
 * thumbnail to the stack to the open overlay. A later decision may fold this into
 * `CardFace`; until then they stay apart.
 *
 * Colour comes from the `--family-<n>-deck-*` tokens (see `theme.css`), keyed by
 * the stable family id. Copy comes from `content/cards` via the card id, so the
 * printed-numbering and retired-family handling is inherited rather than
 * re-derived.
 */
interface DeckCardProps {
  cardId: CardId
  /** "stack" is the 5:7 deck/thumbnail card; "grid" is the 4:5 grid tile. */
  variant?: "stack" | "grid"
  className?: string
}

export default function DeckCard({ cardId, variant = "stack", className }: DeckCardProps) {
  const face = cardFace(cardId)
  // Fall back to family 1 for an unknown id, matching `cardTint`, so a stray id
  // renders a plausible card rather than an unstyled box.
  const family = parseCardId(cardId)?.family ?? 1

  const palette = {
    "--deck-base": `var(--family-${family}-deck-base)`,
    "--deck-glow": `var(--family-${family}-deck-glow)`,
    "--deck-ink": `var(--family-${family}-deck-ink)`,
  } as CSSProperties

  const cls = [styles.card, variant === "grid" && styles.grid, className].filter(Boolean).join(" ")

  return (
    <div className={cls} style={palette}>
      <div className={styles.body}>
        <span className={styles.eyebrow}>{face?.name}</span>
        <span className={styles.statement}>{face?.statement}</span>
      </div>
    </div>
  )
}
